import axios from "@/lib/axios";
import type { RouterType } from "@/router";
import type { TermsAcceptanceStatusDto } from "@fp/admin-api-client";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

export interface UserOrganization {
  id: string;
  organizationId: string;
  userId: string;
  roleCode: string | null;
  isActive: boolean;
  joinDate: string;
  organizationName: string;
  logoUrl: string | null;
}

export interface UserClaims {
  id: string;
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  sid: string;
  emailVerified: boolean;
}

function parseUserClaims(claims: { type: string; value: any }[]): UserClaims {
  const map = new Map<string, any>();
  for (const claim of claims) {
    map.set(claim.type, claim.value);
  }

  const id = map.get("sub");
  const name = map.get("name");
  const firstName = map.get("given_name") ?? "";
  const lastName = map.get("family_name") ?? "";
  const email = map.get("email");
  const sid = map.get("sid");
  const emailVerified = map.get("email_verified") === "true";

  return {
    id,
    name,
    firstName,
    lastName,
    email,
    sid,
    emailVerified,
  };
}

interface UserContextType {
  claims: UserClaims | null;
  organizations: UserOrganization[];
  orgId: string | null;
  setOrgId: (orgId: string) => Promise<void>;
  logout: () => Promise<void>;
  isInitialized: boolean;
  isSwitchingOrganization: boolean;
  hasOrganizations: boolean;
  requiresTermsAcceptance: boolean;
  refetchOrganizations: () => Promise<UserOrganization[]>;
  refetchTermsStatus: () => Promise<void>;
}

export const UserContext = createContext<UserContextType | null>(null);

export const UserContextProvider = ({
  children,
  router,
}: {
  children: React.ReactNode;
  router: RouterType;
}) => {
  const queryClient = useQueryClient();
  const [claims, setClaims] = useState<UserClaims | null>(null);
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [orgId, setOrgIdState] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSwitchingOrganization, setIsSwitchingOrganization] = useState(false);
  const [requiresTermsAcceptance, setRequiresTermsAcceptance] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      try {
        const [userResponse, orgsResponse] = await Promise.all([
          axios.get("/bff/user"),
          axios.get<{ currentOrganizationId: string | null; organizations: UserOrganization[] }>(
            "/api/organizations",
          ),
        ]);

        const userClaims = parseUserClaims(userResponse.data);
        setClaims(userClaims);
        setOrganizations(orgsResponse.data.organizations);
        setOrgIdState(orgsResponse.data.currentOrganizationId);

        try {
          const termsResponse = await axios.get<TermsAcceptanceStatusDto>("/api/legal/terms-of-service/acceptance-status");
          setRequiresTermsAcceptance(termsResponse.data.status === "Required");
        } catch {
          setRequiresTermsAcceptance(false);
        }

        setIsInitialized(true);
      } catch {
        // 401s are handled by the axios interceptor which redirects to /bff/login
      }
    };
    initialize();
  }, []);

  const refetchTermsStatus = async () => {
    const termsResponse = await axios.get<TermsAcceptanceStatusDto>("/api/legal/terms-of-service/acceptance-status");
    setRequiresTermsAcceptance(termsResponse.data.status === "Required");
  };

  const refetchOrganizations = async () => {
    const orgsResponse = await axios.get<{
      currentOrganizationId: string | null;
      organizations: UserOrganization[];
    }>("/api/organizations");

    setOrganizations(orgsResponse.data.organizations);
    setOrgIdState(orgsResponse.data.currentOrganizationId);

    return orgsResponse.data.organizations;
  };

  const setOrgId = async (newOrgId: string) => {
    setIsSwitchingOrganization(true);
    try {
      await axios.put("/api/organization-context", { organizationId: newOrgId });
      setOrgIdState(newOrgId);
      queryClient.clear();
      await router.invalidate();
    } finally {
      setIsSwitchingOrganization(false);
    }
  };

  const logout = async () => {
    if (claims?.sid) {
      window.location.href = `/bff/logout?sid=${claims.sid}`;
    } else {
      window.location.href = "/bff/logout";
    }
  };

  const hasOrganizations = organizations.length > 0;

  const value = useMemo(
    () => ({
      claims,
      organizations,
      orgId,
      setOrgId,
      logout,
      isInitialized,
      isSwitchingOrganization,
      hasOrganizations,
      requiresTermsAcceptance,
      refetchOrganizations,
      refetchTermsStatus,
    }),
    [claims, organizations, orgId, isInitialized, isSwitchingOrganization, hasOrganizations, requiresTermsAcceptance],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }
  return context;
};
